import React, { useEffect, useMemo, useState } from 'react';
import { DragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import Event from 'components/Schedule/Events';
import AppointmentUtils from 'services/AppointmentUtils';
import CalendarUtils from 'services/CalendarUtils';
import { convertDateToDateTime } from 'components/Schedule/TimeZoneAgnosticBigCalendar';
import { SLOT_VALUE_ATTRIBUTE } from 'components/Schedule/SlotTimeTooltip';
import { useAppSelector } from 'careand-redux/store';
import './style.scss';

// The HTML5 drag backend paints a bitmap snapshot of the event taken at dragstart, so the label
// under the cursor keeps advertising the time the appointment *used* to be at. We blank that
// bitmap out and draw a live preview instead, rebuilt from whichever slot the cursor is over.
const DRAG_SOURCE_SELECTOR = '.physician-calendars .rbc-event, .dropzone-appointment-event';

// forwardRef only to absorb the ref DragLayer hands its child; the layer has nothing to expose.
const ScheduleDragPreview = React.forwardRef(
  ({ item, itemType, isDragging, clientOffset }, _ref) => {
    const [source, setSource] = useState(undefined);

    const patients = useAppSelector((state) => state.patients.byId);
    const physicians = useAppSelector((state) => state.physicians.byId);
    const admins = useAppSelector((state) => state.admins.byId);

    useEffect(() => {
      function onDragStart(domEvent) {
        const node = domEvent.target?.closest?.(DRAG_SOURCE_SELECTOR);

        if (!node) {
          setSource(undefined);
          return;
        }

        // HTML5Backend sets its drag image from a listener on `window`, registered when the app's
        // DragDropContext mounted. Ours runs after it, so this is the call that sticks.
        domEvent.dataTransfer?.setDragImage?.(getEmptyImage(), 0, 0);

        const rect = node.getBoundingClientRect();
        setSource({
          width: rect.width,
          height: rect.height,
          offsetX: domEvent.clientX - rect.left,
          offsetY: domEvent.clientY - rect.top,
        });
      }

      window.addEventListener('dragstart', onDragStart);
      return () => window.removeEventListener('dragstart', onDragStart);
    }, []);

    const dragged =
      isDragging && itemType === 'event' && item?.anchor === 'drop' ? item.event : undefined;

    const start = getSlotStart(dragged && clientOffset);

    const previewEvent = useMemo(() => {
      const appointment = dragged?.appointment;
      if (!appointment || start === undefined) return undefined;

      // Mirror what Schedule's moveEvent will actually persist, so the preview can't promise a
      // time the drop doesn't deliver.
      const duration = AppointmentUtils.getDuration(appointment);
      const moved = {
        ...appointment,
        date: start,
        date_end: appointment.date_end
          ? AppointmentUtils.calculateEndDate({ ...appointment, date: start }, duration)
          : undefined,
      };

      return AppointmentUtils.appointmentsToEvents([moved], patients, physicians)[0];
    }, [dragged, start, patients, physicians]);

    if (!previewEvent || !source || !clientOffset) return null;

    return (
      <div
        className="schedule-drag-preview rbc-calendar"
        style={{
          left: clientOffset.x - source.offsetX,
          top: clientOffset.y - source.offsetY,
          width: source.width,
          height: source.height,
        }}
      >
        <div className="day-schedule">
          <div className="rbc-events-container">
            <div className="rbc-event">
              <Event event={previewEvent} physicians={physicians} admins={admins} />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

ScheduleDragPreview.displayName = 'ScheduleDragPreview';

// The slot the cursor is over is also the drop target react-dnd will resolve to: both are the
// `.rbc-time-slot` under the pointer, and dragged events are pointer-events: none meanwhile.
function getSlotStart(clientOffset) {
  if (!clientOffset) return undefined;

  const elements = document.elementsFromPoint(clientOffset.x, clientOffset.y);
  const slot = elements.find((element) => element.hasAttribute?.(SLOT_VALUE_ATTRIBUTE));
  if (!slot) return undefined;

  const value = Number(slot.getAttribute(SLOT_VALUE_ATTRIBUTE));
  if (!Number.isFinite(value)) return undefined;

  return CalendarUtils.convertToTargetTimezone(convertDateToDateTime(new Date(value))).unix();
}

export default DragLayer((monitor) => ({
  item: monitor.getItem(),
  itemType: monitor.getItemType(),
  clientOffset: monitor.getClientOffset(),
  isDragging: monitor.isDragging(),
}))(ScheduleDragPreview);
